import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, ClipboardCheck, ListChecks, Users, Database, LogOut, Home, Image as ImageIcon, Wand2, Award, Briefcase, ShieldCheck, BarChart3, BookOpen, FileEdit, Sparkles, KeyRound, Trophy, Gamepad2, Swords } from "lucide-react";

import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { ADMIN, AUTH } from "@/constants/testIds";
import { LangToggle, UserChip } from "@/components/layout/Nav";
import { Mascot } from "@/components/characters/Characters";
import { CanalesLogo } from "@/components/CanalesLogo";

const READ_ONLY_ROLES = new Set(["admin-lite"]);

export default function AdminGuard({ children }) {
  const { user, loading, logout } = useAuth();
  const { t, lang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const readOnly = READ_ONLY_ROLES.has(user?.role);

  if (loading) {
    return (
      <div className="min-h-screen bg-halftone flex items-center justify-center font-display">
        Loading…
      </div>
    );
  }

  if (!user || !["admin", "owner", "admin-lite"].includes(user.role)) {
    const goToLogin = () => {
      navigate("/login");
    };
    const wrongRole = !!user;
    return (
      <div className="min-h-screen bg-halftone flex items-center justify-center p-6" data-testid="admin-guard-block">
        <div className="brutal p-8 bg-white max-w-md text-center">
          <Mascot size={100} />
          <h2 className="mt-4 font-display font-black text-3xl">Admin Panel</h2>
          {wrongRole ? (
            <>
              <p className="mt-2 font-body">
                {lang === "es" ? (
                  <>Tu cuenta <b>{user.email}</b> no tiene permisos de admin.
                  Pide al owner que te agregue en Ajustes → Cuentas y te suba a rol admin.</>
                ) : (
                  <>Your account <b>{user.email}</b> is not admin. Ask the owner
                  to add you in Settings → Accounts and promote you to admin.</>
                )}
              </p>
              <button
                onClick={() => { logout(); navigate("/"); }}
                data-testid="admin-guard-logout"
                className="mt-4 brutal-btn bg-canales-red text-white px-6 py-3 text-lg w-full"
              >
                <LogOut className="inline w-4 h-4 mr-1" />
                {lang === "es" ? "Salir" : "Sign out"}
              </button>
            </>
          ) : (
            <>
              <p className="mt-2 font-body">
                {lang === "es"
                  ? "Solo el equipo Canales autorizado puede entrar. Usa tu email corporativo y contraseña."
                  : "Only authorized Canales staff can enter. Use your corporate email and password."}
              </p>
              <button
                onClick={goToLogin}
                data-testid={AUTH.loginBtn}
                className="mt-4 brutal-btn bg-canales-blue text-white px-6 py-3 text-lg w-full"
              >
                <KeyRound className="inline w-4 h-4 mr-1" />
                {lang === "es" ? "Iniciar sesión" : "Sign in"}
              </button>
            </>
          )}
          <Link to="/" className="mt-3 inline-flex items-center gap-1 text-sm font-display font-bold underline">
            <Home className="w-4 h-4" /> Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  const links = [
    { to: "/admin", label: t("dashboard"), Icon: LayoutDashboard, testid: ADMIN.navDashboard },
    { to: "/admin/stats", label: t("stats"), Icon: BarChart3, testid: "admin-nav-stats" },
    { to: "/admin/team-ranking", label: lang === "es" ? "Ranking Equipos" : "Team Ranking", Icon: Trophy, testid: "admin-nav-team-ranking" },
    { to: "/admin/dijeron", label: lang === "es" ? "100 Canales Dijeron" : "Canales Feud", Icon: Gamepad2, testid: "admin-nav-dijeron" },
    { to: "/admin/duelo-equipos", label: lang === "es" ? "Duelo de Equipos" : "Team Duel", Icon: Swords, testid: "admin-nav-team-duel" },
    { to: "/admin/exams", label: t("exams"), Icon: ClipboardCheck, testid: ADMIN.navExams },
    { to: "/admin/responses", label: t("responses"), Icon: ListChecks, testid: ADMIN.navResponses },
    { to: "/admin/employees", label: t("employees"), Icon: Users, testid: ADMIN.navEmployees },
    { to: "/admin/questions", label: t("questions"), Icon: Database, testid: ADMIN.navQuestions },
    { to: "/admin/illustrations", label: t("illustrations"), Icon: ImageIcon, testid: "admin-nav-illustrations" },
    { to: "/admin/comic-illustrations", label: lang === "es" ? "Cómics AI" : "Comics AI", Icon: BookOpen, testid: "admin-nav-comic-illu" },
    { to: "/admin/comic-content", label: lang === "es" ? "Editar Cómics" : "Edit Comics", Icon: FileEdit, testid: "admin-nav-comic-content" },
    { to: "/admin/ai-generator", label: t("aiGenerator"), Icon: Wand2, testid: "admin-nav-ai-generator" },
    { to: "/admin/certificates", label: t("certificates"), Icon: Award, testid: "admin-nav-certificates" },
    { to: "/admin/certificate-designer", label: lang === "es" ? "Diseñador AI" : "AI Designer", Icon: Sparkles, testid: "admin-nav-cert-designer" },
    { to: "/admin/staff", label: lang === "es" ? "Cuentas" : "Accounts", Icon: KeyRound, testid: "admin-nav-staff" },
    { to: "/admin/permissions", label: lang === "es" ? "Permisos" : "Permissions", Icon: ShieldCheck, testid: "admin-nav-permissions" },
    { to: "/admin/roles", label: t("roles"), Icon: Briefcase, testid: "admin-nav-roles" },
    { to: "/admin/settings", label: t("settings"), Icon: ShieldCheck, testid: "admin-nav-settings" },
  ];

  return (
    <div className="min-h-screen bg-halftone">
      <header className="sticky top-0 z-40 bg-canales-blue text-white border-b-4 border-black">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3">
            <CanalesLogo width={130} />
            <div>
              <div className="font-display font-black leading-none">ADMIN</div>
              <div className="font-body text-xs leading-none mt-1 opacity-80">{user.email}</div>
            </div>
          </Link>
          <nav className="hidden md:flex flex-wrap gap-1">
            {links.map(({ to, label, Icon, testid }) => {
              const active = location.pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={testid}
                  className={`px-3 py-1.5 rounded-full font-display font-bold text-sm border-2 border-black flex items-center gap-2 ${
                    active ? "bg-canales-yellow text-black" : "bg-white text-black"
                  }`}
                >
                  <Icon className="w-4 h-4" /> {label}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-2">
            <UserChip />
            <LangToggle />
            <button
              onClick={() => { logout(); navigate("/"); }}
              className="brutal-btn bg-white text-black px-3 py-1.5 text-sm flex items-center gap-1"
              data-testid={AUTH.logoutBtn}
            >
              <LogOut className="w-4 h-4" /> {t("logout")}
            </button>
          </div>
        </div>
        <div className="md:hidden flex flex-wrap gap-1 px-4 pb-3">
          {links.map(({ to, label, Icon, testid }) => (
            <Link key={to} to={to} data-testid={testid + "-m"} className="brutal-btn bg-white text-black px-2 py-1 text-xs flex items-center gap-1">
              <Icon className="w-3 h-3" /> {label}
            </Link>
          ))}
        </div>
      </header>
      {readOnly && (
        <div className="bg-canales-yellow border-b-4 border-black" data-testid="admin-readonly-banner">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-2 font-display font-black text-sm">
            <span className="uppercase tracking-widest">Modo Solo Lectura</span>
            <span className="font-body font-normal">
              — {lang === "es"
                ? "Puedes ver todo, pero no editar ni borrar. Pide al Owner ser Admin Completo para tener permisos de edición."
                : "You can view everything, but not edit or delete. Ask the Owner for full-admin rights."}
            </span>
          </div>
        </div>
      )}
      <main className="max-w-7xl mx-auto px-4 py-8">{children}</main>
    </div>
  );
}
